import { useMemo, useState } from 'react';
import {
  resolveExecutionRunsToolbarDates,
  resolveExecutionRunsToolbarQuickFilter,
} from '@/features/executionRuns/pages/executionRunsToolbar';
import {
  formatRangeWindowNoDataText,
  type QualityRange,
  useQualityDateRange,
} from '@/features/quality/hooks';
import {
  buildDateRangeFilters,
  buildInFilter,
  mergeFilters,
} from '@/ui/composites/grid/gridFilters';
import type { ApiFilter } from '@/ui/composites/grid/gridUtils';

type UseExecutionRunsTableToolbarStateParams<Status extends string> = {
  dateField: string;
  noRowsBaseText: string;
  initialStatuses?: Status[];
  controlledDateState?: {
    range: QualityRange;
    setRange: (value: QualityRange) => void;
    startDay: string | null;
    setStartDay: (value: string | null) => void;
    endDay: string | null;
    setEndDay: (value: string | null) => void;
  };
};

type UseExecutionRunsTableToolbarStateResult<Status extends string> = {
  search: string;
  setSearch: (value: string) => void;
  selectedStatuses: Status[];
  setSelectedStatuses: (value: Status[]) => void;
  selectedTags: string[];
  setSelectedTags: (value: string[]) => void;
  fixedFilters: ApiFilter[];
  noRowsText: string;
  dateQuickFilter: '7d' | '14d' | '30d' | 'latest';
  onDateQuickFilterChange: (value: QualityRange) => void;
  fromDate: ReturnType<typeof resolveExecutionRunsToolbarDates>['fromDate'];
  toDate: ReturnType<typeof resolveExecutionRunsToolbarDates>['toDate'];
  onFromDateChange: (value: string | null) => void;
  onToDateChange: (value: string | null) => void;
  latestQueryStartDay?: string | null;
  latestQueryEndDay?: string | null;
};

export const useExecutionRunsTableToolbarState = <Status extends string>({
  dateField,
  noRowsBaseText,
  initialStatuses = [],
  controlledDateState,
}: UseExecutionRunsTableToolbarStateParams<Status>): UseExecutionRunsTableToolbarStateResult<Status> => {
  const [search, setSearch] = useState('');
  const [selectedStatuses, setSelectedStatuses] = useState<Status[]>(initialStatuses);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [internalRange, setInternalRange] = useState<QualityRange>('7d');
  const [internalStartDay, setInternalStartDay] = useState<string | null>(null);
  const [internalEndDay, setInternalEndDay] = useState<string | null>(null);
  const range = controlledDateState?.range ?? internalRange;
  const setRange = controlledDateState?.setRange ?? setInternalRange;
  const startDay = controlledDateState?.startDay ?? internalStartDay;
  const setStartDay = controlledDateState?.setStartDay ?? setInternalStartDay;
  const endDay = controlledDateState?.endDay ?? internalEndDay;
  const setEndDay = controlledDateState?.setEndDay ?? setInternalEndDay;
  const dashboardRange = useQualityDateRange(range);
  const hasCustomDates = Boolean(startDay ?? endDay);
  const dateRangeFilters = useMemo(
    () =>
      buildDateRangeFilters(dateField, dashboardRange.rangeStartIso, dashboardRange.rangeEndIso),
    [dashboardRange.rangeEndIso, dashboardRange.rangeStartIso, dateField],
  );
  const selectedDayFilters = useMemo(() => {
    if (!startDay && !endDay) return [];
    const start = startDay ? `${startDay}T00:00:00.000Z` : null;
    const end = endDay ? `${endDay}T23:59:59.999Z` : null;
    return buildDateRangeFilters(dateField, start, end);
  }, [dateField, endDay, startDay]);
  const fixedFilters = useMemo(
    () =>
      mergeFilters(
        selectedDayFilters,
        dateRangeFilters,
        buildInFilter('status', selectedStatuses),
        buildInFilter('tags', selectedTags),
      ),
    [dateRangeFilters, selectedDayFilters, selectedStatuses, selectedTags],
  );
  const noRowsText = formatRangeWindowNoDataText(noRowsBaseText, range, hasCustomDates);
  const toolbarDates = resolveExecutionRunsToolbarDates({ hasCustomDates, startDay, endDay });
  const dateQuickFilter = resolveExecutionRunsToolbarQuickFilter(range);

  return {
    search,
    setSearch,
    selectedStatuses,
    setSelectedStatuses,
    selectedTags,
    setSelectedTags,
    fixedFilters,
    noRowsText,
    dateQuickFilter,
    onDateQuickFilterChange: (value) => {
      setRange(value);
      setStartDay(null);
      setEndDay(null);
    },
    fromDate: toolbarDates.fromDate,
    toDate: toolbarDates.toDate,
    onFromDateChange: setStartDay,
    onToDateChange: setEndDay,
  };
};
